import { useMutation, useQuery } from "@apollo/client";
import { useState } from "react";
import { GET_SELLER_STORE } from "../../api/seller-store";
import { Order } from "../../../../__generated__/graphql";
import { notify, notifyError } from "../../../../utils/toast";
import LoadingSpinnerInline from "../../../../components/common/loading-spinner/loading-spinner-inline";
import {
  GET_PACK_ORDERS,
  ORDER_FILTERS,
  CREATE_REPORT,
} from "../../api/pack-order-queries";
import {
  CONFIRM_PACK_ORDER,
  CANCEL_PACK_ORDER,
} from "../../api/pack-order-mutations";
import { TOGGLE_PICKUP_COMPLETED } from "../../../../api/order-mutations";
import CancelPackOrderModal from "../../components/cancel-pack-order-modal";
import OrderCard from "./order-card";
import FilterBar from "./filter-bar";
import { FilterStatus } from "./filter-status-chip";
import { StartPreOrderCancelSelectionButton } from "./start-pre-order-cancel-selection-button";
import { useCancelSelectionMode } from "../hooks/use-cancel-selection-mode";
import { useCancelOrders } from "../hooks/use-cancel-orders";
import ConfirmOrderModal from "../components/confirm-order-modal";
import CancelOrderConfirmModal from "../components/cancel-order-confirm-modal";
import { clsx } from "clsx";

const PreOrderList = ({
  setIsOverlayModeStarted,
}: {
  setIsOverlayModeStarted: (value: boolean) => void;
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isReportModalOpen, setReportModalOpen] = useState(false);
  const [reportReason, setReportReason] = useState<string>("");
  const [selectedFilter, setSelectedFilter] = useState<FilterStatus>("ALL"); // 전체 주문 선택 기본값
  const { data, refetch } = useQuery(GET_PACK_ORDERS, {
    fetchPolicy: "network-only",
  });
  const [createReport, { loading: reportLoading }] = useMutation(CREATE_REPORT);

  const { data: storeData } = useQuery(GET_SELLER_STORE, {
    fetchPolicy: "network-only",
  });

  const [confirmPackOrder] = useMutation(CONFIRM_PACK_ORDER, {
    refetchQueries: [GET_PACK_ORDERS],
  });

  const [cancelPackOrder] = useMutation(CANCEL_PACK_ORDER, {
    refetchQueries: [GET_PACK_ORDERS],
  });

  const [togglePickupCompleted] = useMutation(TOGGLE_PICKUP_COMPLETED, {
    refetchQueries: [GET_PACK_ORDERS],
  });

  const today = new Date();
  const isTodayAndYesterday = (dateString: string) => {
    const date = new Date(dateString);
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    return (
      (date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()) ||
      (date.getDate() === yesterday.getDate() &&
        date.getMonth() === yesterday.getMonth() &&
        date.getFullYear() === yesterday.getFullYear())
    );
  };
  const todayOrders = data?.packOrdersBySeller?.filter((order: Order) =>
    isTodayAndYesterday(order.createdAt),
  );
  const filteredTodayOrders: Order[] = todayOrders?.filter(
    ORDER_FILTERS[selectedFilter],
  );

  // 선택하여 취소하기 모드 관리
  const {
    isModeStarted: isCancelSelectionModeStarted,
    setIsModeStarted: setIsCancelSelectionModeStarted,
    selectedOrderIds,
    isConfirmModalOpen: isCancelSelectionConfirmModalOpen,
    setIsConfirmModalOpen: setIsCancelSelectionConfirmModalOpen,
    toggleSelection: handleToggleCancelSelection,
    toggleAllSelection: handleToggleAllCancelSelection,
    resetSelectionMode: stopCancelSelectionMode,
  } = useCancelSelectionMode(filteredTodayOrders);

  // 주문 취소 서버 요청
  const { cancelOrders, loading: isConfirmLoading } = useCancelOrders();

  // 주문 취소 함수 (UI 상태도 함께 변경)
  const handleCancelOrders = async () => {
    await cancelOrders(selectedOrderIds);
    setIsCancelSelectionConfirmModalOpen(false);
    setIsCancelSelectionModeStarted(false);
    setIsOverlayModeStarted(false);
  };

  const handleConfirmOrder = (orderId: number) => {
    setSelectedOrderId(orderId);
    setIsConfirmModalOpen(true);
  };

  const handleCancelOrder = (orderId: number) => {
    setSelectedOrderId(orderId);
    setIsModalOpen(true);
  };
  const confirmOrder = () => {
    if (selectedOrderId !== null) {
      confirmPackOrder({
        variables: { orderId: selectedOrderId },
        onCompleted: () => {
          notify("예약팩이 확정되었습니다.");
        },
        onError: (error) => {
          notifyError(error.message);
          refetch();
        },
      });
      setIsConfirmModalOpen(false);
    }
  };

  const confirmCancelOrder = () => {
    // MEMO: 2025.07.25부로 사장님이 설정한 메시지로 고정 발송함
    // 취소 사유 선택 모달 제거하고 확인 모달만 띄우게 되어 사유는 따로 입력하지 않음
    if (selectedOrderId !== null) {
      cancelPackOrder({
        variables: { orderId: selectedOrderId, cancelReason: "" },
        onCompleted: () => {
          notify("예약팩이 취소되었습니다.");
        },
        onError: (error) => {
          notifyError(error.message);
          refetch();
          return;
        },
      });
      setIsModalOpen(false);
    }
  };

  const isTomorrowOrder = (order: Order) => {
    const pickUpDate = new Date(order.packPickupStartTime);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1); // 내일 날짜로 설정

    return pickUpDate.toDateString() === tomorrow.toDateString(); // 날짜 비교
  };

  const 픽업마감시간 = (reserveTime: any) => {
    const now = new Date();
    const dayOfWeek = ((now.getDay() + 6) % 7).toString(); // 월요일을 0으로, 일요일을 6으로 설정
    const endTime = reserveTime?.[dayOfWeek]?.end;
    if (endTime) {
      const [hours, minutes] = endTime.split(":").map(Number);
      const endDate = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        hours,
        minutes,
      );
      return endDate.toLocaleTimeString();
    }
    return null;
  };

  const handleReportOrder = (orderId: number, reason: string) => {
    createReport({
      variables: { orderId, reason },
      onCompleted: () => {
        notify("신속하게 확인 후에 연락드리겠습니다.");
      },
    });
  };

  const handleCompletePickup = (orderId: number) => {
    togglePickupCompleted({
      variables: { id: orderId, triggeredBy: "seller" },
    });
  };

  return (
    <div
      className={clsx(
        "w-full",
        isCancelSelectionModeStarted && "pointer-events-auto", // 모드 시작 시 클릭 가능
      )}
    >
      <hr className="my-4" />
      <div
        className={clsx(
          "w-full",
          isCancelSelectionModeStarted && "pointer-events-none", // 필터 부분만 클릭 방지
        )}
      >
        <ul className="w-full max-w-xl mx-auto">
          <div className={"flex justify-between items-center mt-8"}>
            <h3 className="text-3xl font-bold text-gray-800">📦 주문현황</h3>
            {filteredTodayOrders?.length > 0 && (
              <span className="text-lg text-gray-500 ml-2 leading-none">
                {selectedFilter !== "ALL" && (
                  <>
                    <span className="font-bold">
                      {filteredTodayOrders?.length}개
                    </span>{" "}
                    /{" "}
                  </>
                )}
                <span className="text-md">전체 {todayOrders?.length}개</span>
              </span>
            )}
          </div>

          <div className="space-y-2 mb-4">
            <FilterBar
              selectedStatus={selectedFilter}
              onStatusChange={setSelectedFilter}
            />

            {(selectedFilter === "ALL" || selectedFilter === "PENDING") && (
              <StartPreOrderCancelSelectionButton
                onClick={() => {
                  setIsCancelSelectionModeStarted(true);
                  setIsOverlayModeStarted(true);
                  notify("취소하고자 하는 주문을 선택해주세요!");
                }}
                className="bg-white"
              />
            )}
          </div>
        </ul>
      </div>

      {/* OrderCard 영역 */}
      {filteredTodayOrders?.length > 0 ? (
        <div
          className={clsx(
            "w-full max-w-xl mx-auto",
            isCancelSelectionModeStarted && "relative z-20",
          )}
        >
          <div className="rounded-lg space-y-4">
            {[...filteredTodayOrders].reverse().map((order: Order) => (
              <OrderCard
                key={order.id}
                order={order}
                countdown={null}
                handleConfirmOrder={handleConfirmOrder}
                handleCancelOrder={handleCancelOrder}
                handleCompletePickup={handleCompletePickup}
                isPickupCompleted={order.isPickupCompleted || null}
                isTomorrowOrder={isTomorrowOrder}
                isCancelSelectionModeStarted={isCancelSelectionModeStarted}
                isCancelSelected={selectedOrderIds.includes(order.id)}
                handleToggleCancelSelection={() => {
                  handleToggleCancelSelection(order);
                }}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="w-full">
          <div className="bg-white w-full rounded-lg p-4">
            <p className=" text-gray-600">
              {selectedFilter === "ALL"
                ? "아직은 랜덤팩을 가지러 올 사람이 없네요."
                : `선택한 필터에 해당하는 주문이 없어요.`}
            </p>
          </div>
        </div>
      )}

      {/* 선택 모드일 때 하단 고정 액션 바 */}
      {isCancelSelectionModeStarted && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 z-50">
          <div className="flex items-center justify-between max-w-xl mx-auto">
            <div className="flex items-center gap-3">
              <button
                className={clsx(
                  "text-gray-500 px-4 py-2 rounded-lg",
                  selectedOrderIds.length === 0
                    ? "bg-blue-500 text-white"
                    : "bg-gray-300 text-gray-800",
                )}
                onClick={handleToggleAllCancelSelection}
              >
                {selectedOrderIds.length === 0 ? "모두 선택" : "모두 해제"}
              </button>
              <span className="text-gray-600 font-medium">
                {selectedOrderIds.length}개 선택됨
              </span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  stopCancelSelectionMode();
                  setIsOverlayModeStarted(false);
                }}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg"
              >
                취소
              </button>
              <button
                onClick={() => setIsCancelSelectionConfirmModalOpen(true)}
                className="px-4 py-2 bg-red-500 text-white rounded-lg"
              >
                완료
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 선택 모드일 때 하단 패딩 추가 */}
      {isCancelSelectionModeStarted && <div className="h-20"></div>}

      <CancelPackOrderModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={confirmCancelOrder}
        cancelMessage={storeData?.getSellerStore?.pack?.cancelMessage}
        onEditMessage={() =>
          (window.location.href = "/boss/pre-order/pack-management")
        }
      />
      <ConfirmOrderModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={confirmOrder}
        pickupEndTime={픽업마감시간(storeData?.getSellerStore?.reserveTime)}
      />

      <CancelOrderConfirmModal
        isOpen={isCancelSelectionConfirmModalOpen}
        onClose={() => setIsCancelSelectionConfirmModalOpen(false)}
        onConfirm={handleCancelOrders}
        isConfirmLoading={isConfirmLoading}
        selectedOrderCount={selectedOrderIds.length}
      />
      {isReportModalOpen && selectedOrderId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-8 min-w-[340px] max-w-[90vw] rounded shadow-lg">
            <div className="text-2xl font-bold mb-6 text-center">
              이 손님이 맞나요?
            </div>
            <div className="bg-gray-100 rounded-lg p-4 mb-4">
              <div className="text-xl text-gray-600 mb-1">
                이름:{" "}
                <span className="font-bold text-blue-700">
                  {data?.packOrdersBySeller.find(
                    (order: any) => order.id === selectedOrderId,
                  )?.user?.name || "익명의 히어로"}
                </span>
              </div>
              <div className="text-xl text-gray-600">
                전화번호:{" "}
                <span className="font-bold text-blue-700">
                  {data?.packOrdersBySeller.find(
                    (order: any) => order.id === selectedOrderId,
                  )?.user?.phone || "없음"}
                </span>
              </div>
            </div>
            <div className="text-xl text-gray-800 font-semibold mb-6">
              어떤 상황인지 알려주세요.
              <br />
              빠르게 검토하고 연락드릴게요!
            </div>
            <div className="text-gray-600 mb-4">
              <span className="font-bold">예시:</span>
              <ul>
                <li>· 손님이 오지 않았나요?</li>
                <li>· 손님이 무리한 요구를 했나요?</li>
                <li>· 손님이 픽업시간보다 늦게 왔나요?</li>
              </ul>
            </div>
            <textarea
              className="w-full p-3 border border-gray-300 rounded mb-4 text-lg"
              value={reportReason}
              rows={5}
              onChange={(e: any) => setReportReason(e.target.value)}
              placeholder="상황을 입력해 주세요"
            />
            <div className="flex justify-center gap-4 mt-4">
              <button
                className="bg-gray-500 text-white px-5 py-2 rounded"
                onClick={() => {
                  setReportModalOpen(false);
                  setReportReason("");
                }}
              >
                취소
              </button>
              <button
                className="bg-blue-500 text-white px-5 py-2 rounded"
                onClick={() => {
                  if (window.confirm("문의를 제출할까요?")) {
                    handleReportOrder(selectedOrderId, reportReason);
                    setReportModalOpen(false);
                    setReportReason("");
                  }
                }}
              >
                {reportLoading ? <LoadingSpinnerInline /> : "완료"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreOrderList;
